import { Helmet } from "react-helmet-async";
import { mediaUrl } from "@/lib/api";

/**
 * Storefront SEO tags. Ad scripts are loaded by StoreAds (Helmet does not run scripts).
 * Search Console verification stays here because it is a meta tag, not a script.
 */
const SAFE_ID = /^[A-Za-z0-9._-]{2,128}$/;

function safeId(v) {
  const s = String(v || "").trim();
  return SAFE_ID.test(s) ? s : "";
}

function faviconType(url) {
  const path = String(url || "").split("?")[0].toLowerCase();
  if (path.endsWith(".svg")) return "image/svg+xml";
  if (path.endsWith(".ico")) return "image/x-icon";
  if (path.endsWith(".webp")) return "image/webp";
  if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
  if (path.endsWith(".gif")) return "image/gif";
  return "image/png";
}

export default function StoreHead({ seo, ads, storeInfo, branding, noIndex = false }) {
  const s = seo || {};
  const a = ads || {};
  const info = storeInfo || {};
  const brand = branding || {};
  const keywords = (s.meta_keywords || "").trim();
  const robots = (noIndex || s.robots_index === false) ? "noindex, nofollow" : "index, follow";
  const lang = (s.default_language || "en").trim() || "en";
  const twitter = (s.twitter_handle || "").replace(/^@/, "").trim();
  const bing = safeId(s.bing_site_verification);
  const place = (s.geo_placename || "").trim();
  const region = (s.geo_region || "").trim();
  const gsc = safeId(a.google_site_verification);
  const jsonLdOn = s.json_ld_enabled !== false;
  const storeName = info.name || "";

  const orgLd = jsonLdOn && storeName ? {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    name: storeName,
    telephone: info.phone || undefined,
    email: info.email || undefined,
    address: info.address ? {
      "@type": "PostalAddress",
      streetAddress: info.address,
      addressLocality: place || undefined,
      addressRegion: region || undefined,
      addressCountry: "IN",
    } : undefined,
  } : null;

  const pageTitle = (s.meta_title || "").trim() || storeName;
  const rawFav = (brand.favicon_url || "").trim();
  const favicon = rawFav ? mediaUrl(rawFav) : "";
  const favType = favicon ? faviconType(rawFav) : "";

  return (
    <Helmet>
      <html lang={lang} />
      {pageTitle ? <title>{pageTitle}</title> : null}
      {favicon ? <link rel="icon" href={favicon} type={favType} data-testid="store-favicon" /> : null}
      {favicon ? <link rel="shortcut icon" href={favicon} /> : null}
      {favicon ? <link rel="apple-touch-icon" href={favicon} /> : null}
      {(s.meta_description || "").trim() ? <meta name="description" content={s.meta_description.trim()} /> : null}
      <meta name="robots" content={robots} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {(s.focus_keyword || "").trim() ? <meta name="news_keywords" content={s.focus_keyword.trim()} /> : null}
      {twitter ? <meta name="twitter:site" content={`@${twitter}`} /> : null}
      {(s.og_title || pageTitle || "").trim() ? <meta property="og:title" content={(s.og_title || pageTitle).trim()} /> : null}
      {storeName ? <meta property="og:site_name" content={storeName} /> : null}
      {gsc ? <meta name="google-site-verification" content={gsc} /> : null}
      {bing ? <meta name="msvalidate.01" content={bing} /> : null}
      {place ? <meta name="geo.placename" content={place} /> : null}
      {region ? <meta name="geo.region" content={region} /> : null}

      {orgLd ? <script type="application/ld+json">{JSON.stringify(orgLd)}</script> : null}
    </Helmet>
  );
}
